import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { HelpTooltip } from '@/presentation/components/common/HelpTooltip';
import { useKeyEsc } from '@/presentation/hooks/useKeyEsc';
import {
  metricGlossaryKey,
  type MetricDefinitionId,
} from '@/application/metrics/metricDefinitions';

const SHEET_Z_INDEX = 12000;

function readPreferSheet(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return (
    window.matchMedia('(pointer: coarse)').matches ||
    window.matchMedia('(max-width: 767px)').matches
  );
}

/** Touch o viewport estrecho: bottom sheet. Desktop: tooltip. */
function usePreferMetricHelpSheet(): boolean {
  const [preferSheet, setPreferSheet] = useState(readPreferSheet);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;

    const coarse = window.matchMedia('(pointer: coarse)');
    const narrow = window.matchMedia('(max-width: 767px)');
    const sync = () => setPreferSheet(coarse.matches || narrow.matches);
    sync();
    coarse.addEventListener('change', sync);
    narrow.addEventListener('change', sync);
    return () => {
      coarse.removeEventListener('change', sync);
      narrow.removeEventListener('change', sync);
    };
  }, []);

  return preferSheet;
}

export interface MetricDefinitionTooltipProps {
  metricId: MetricDefinitionId;
  size?: 'sm' | 'md';
  align?: 'start' | 'end';
  className?: string;
  stopPropagation?: boolean;
}

function GlossaryBody({
  name,
  definition,
  notes,
  notesLabel,
}: {
  name: string;
  definition: string;
  notes: string;
  notesLabel: string;
}) {
  return (
    <div className="space-y-1.5 text-left">
      <p className="text-[11px] font-semibold leading-snug text-white">{name}</p>
      <p className="text-[11px] leading-snug text-gray-300">{definition}</p>
      <p className="text-[10px] leading-snug text-gray-400">
        <span className="font-semibold uppercase tracking-wide">{notesLabel}. </span>
        {notes}
      </p>
    </div>
  );
}

/**
 * “?” de glosario: tooltip en desktop, bottom sheet en touch / mobile.
 * Copy solo desde `metrics.glossary.{id}` (dueño del glosario).
 */
export function MetricDefinitionTooltip({
  metricId,
  size = 'sm',
  align = 'end',
  className = '',
  stopPropagation = true,
}: MetricDefinitionTooltipProps) {
  const { t } = useTranslation();
  const preferSheet = usePreferMetricHelpSheet();
  const [sheetOpen, setSheetOpen] = useState(false);

  const name = t(metricGlossaryKey(metricId, 'name'));
  const definition = t(metricGlossaryKey(metricId, 'definition'));
  const notes = t(metricGlossaryKey(metricId, 'notes'));
  const notesLabel = t('metrics.glossary.notesLabel');
  const ariaLabel = t('metrics.glossary.whatIs', { name });

  const closeSheet = useCallback(() => setSheetOpen(false), []);
  useKeyEsc(closeSheet, sheetOpen);

  const body = (
    <GlossaryBody name={name} definition={definition} notes={notes} notesLabel={notesLabel} />
  );

  if (!preferSheet) {
    return (
      <HelpTooltip
        content={body}
        ariaLabel={ariaLabel}
        size={size}
        align={align}
        className={className}
        stopPropagation={stopPropagation}
      />
    );
  }

  return (
    <>
      <span className={`inline-flex shrink-0 ${className}`}>
        <button
          type="button"
          data-testid={`metric-glossary-${metricId}`}
          aria-label={ariaLabel}
          aria-expanded={sheetOpen}
          className={[
            'inline-flex items-center justify-center rounded-full',
            'bg-gray-200 text-gray-500 font-bold dark:bg-gray-600 dark:text-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-teal-500',
            size === 'sm' ? 'h-3.5 w-3.5 text-[9px]' : 'h-4 w-4 text-[10px]',
          ].join(' ')}
          onClick={(event) => {
            if (stopPropagation) event.stopPropagation();
            setSheetOpen(true);
          }}
        >
          ?
        </button>
      </span>
      {sheetOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0" style={{ zIndex: SHEET_Z_INDEX }} data-testid="metric-glossary-sheet">
            <button
              type="button"
              className="absolute inset-0 bg-black/40"
              aria-label={t('metrics.glossary.close')}
              onClick={closeSheet}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby={`metric-glossary-title-${metricId}`}
              className="absolute inset-x-0 bottom-0 max-h-[70vh] overflow-y-auto rounded-t-2xl border border-[var(--bi-line)] bg-[var(--color-surface)] px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 shadow-2xl"
            >
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-300 dark:bg-slate-600" aria-hidden />
              <div className="mb-2 flex items-start justify-between gap-3">
                <h2
                  id={`metric-glossary-title-${metricId}`}
                  className="text-base font-semibold text-gray-950 dark:text-white"
                >
                  {name}
                </h2>
                <button
                  type="button"
                  onClick={closeSheet}
                  className="rounded-lg px-2 py-1 text-sm font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-gray-800"
                >
                  {t('metrics.glossary.close')}
                </button>
              </div>
              <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">{definition}</p>
              <p className="mt-3 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                <span className="font-semibold uppercase tracking-wide">{notesLabel}. </span>
                {notes}
              </p>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
